import useUser from '@/composables/useUser';
import Bestandsaufnahme from '@/models/ba/Bestandsaufnahme';
import { BestandsaufnahmeJson } from '@/models/ba/interfaces/IBestandsaufnahme';
import BestandsaufnahmeModel from '@/models/ba/models/bestandsaufnahme.model';
import Immobilie, { immobilie } from '@/models/immobilie.model';
import { ICurrentHzba } from '@/store/store.types';
import {
  loadHzbaPhotosLocal,
  saveHzbaPhotosLocal
} from '@/utilities/photo-storage';
import { Module } from 'vuex';
import { useBestandsaufnahmens } from '@/composables/Bestandsaufnahme/useBestandsaufnahmens';
import { logger } from "@/utilities/logging";

export const HZBA_MODE = {
  MODAL: 'modal',
  SUBMODAL: 'subModal',
};

export const currentHzba: Module<ICurrentHzba, any> = {
  namespaced: true,
  state: () => ({
    currentHzba: {},
    currentBa: undefined,
    photoUploads: undefined,
    uploadStatus: undefined,
    subModalHzba: undefined,
    modalHzba: undefined,
    pleasePauseSync: false,
    modalBas: [],
  }),
  mutations: {
    setCurrentBa(state: ICurrentHzba, payload: Bestandsaufnahme) {
      state.currentBa = payload;
    },
    /**
     * Adds temporary ba every time a modal is opened.
     */
    async addModalBa(state: ICurrentHzba, depth: number ) {

      if (!state.currentBa) {
        logger.error(`Error setting setModalBa: currentBa undefined`);
        return console.error('Error setting setModalBa: currentBa undefined');
      }

      if (depth !== state.modalBas.length) {
        console.error('Tried to setModalBa but depth number is too high');
        // logger.error(`{"message": "Tried to setModalBa but depth number is too high"}`);

        return;
      }

      const baJson = depth === 0 ? await state.currentBa.copyJson() : await state.modalBas[depth-1].toClassJson();
      const ba = new Bestandsaufnahme(baJson, depth);
      state.modalBas.push(ba)
    },

    /**
     * Removes temporary ba. Does nothing say about whether this ba has been saved to next (or top) level or has been discarded.
     */
    removeModalBa(state: ICurrentHzba) {
      state.modalBas.pop();
    },
    cleanCurrentHzba(state: ICurrentHzba) {
      // state.currentBa = undefined
    },
    setUploadStatus(state: ICurrentHzba, payload) {
      state.uploadStatus = payload;
    },
    setPhotoUploadTasks(state: ICurrentHzba, payload) {
      state.photoUploads = payload;
    },
    setPleasePauseSync(state: ICurrentHzba, payload) {
      state.pleasePauseSync = payload;
    }
  },
  actions: {
    async setCurrentHzba({ state }: { state: ICurrentHzba }, ba: BestandsaufnahmeModel ) {
      if (!ba) {
        console.warn('[currentHzba/setCurrentHzba] setting ba failed because param ba is null or undefined');
        // logger.warn(`{"message": "[currentHzba/setCurrentHzba] setting ba failed because param ba is null or undefined"}`);
        state.currentBa = undefined;
        return;
      }

      state.currentHzba = {};
      const currentBaJson = await ba.copyJson();

      /**
       * local testing:
       */
      // @ts-ignore
      // const currentBaJson = useGenericHzbaJsonInProgress().data as BestandsaufnahmeJson;
      // const currentBaJson = useNewHzbaJson().data as BestandsaufnahmeJson;

      const currentBa = new Bestandsaufnahme(currentBaJson, undefined);
      currentBa.fragenblocks?.forEach((el) =>
        el.setupFragenblockMinimumInstances(),
      );

      logger.defaultMeta.currentBa = currentBa.id;

      console.log('[currentHzba/setCurrentHzba] set current ba with content:', await currentBa.toClassJson());
      // logger.info(`{"message": "[currentHzba/setCurrentHzba] set current ba ${currentBa.id} with content}"}`); 
      state.currentBa = currentBa;
      await loadHzbaPhotosLocal(state.currentBa);
    },

    /**
     * Todo refactor this function and move to useBestandsaufnahmes
     * @param state
     * @param storeToStore
     */
    async refetchCurrentBa({ state }: { state: any }, storeToStore?: boolean) {
      const res = await BestandsaufnahmeModel.api().get(
          `/bestandsaufnahmes/${state.currentBa!.id}`
      );
      const newBa = (res.getDataFromResponse() as any)
          .data as BestandsaufnahmeJson;

      const ba = new Bestandsaufnahme(newBa, undefined);
      ba.fragenblocks?.forEach((el) =>
          el.setupFragenblockMinimumInstances(),
      );

      const isDownloaded = state.currentBa?.isDownloaded;

      state.currentBa = ba;

      if (!storeToStore) {
        return state.currentBa;
      }

      await BestandsaufnahmeModel.update({
        where: newBa.id,
        data: newBa,
      });

      if (isDownloaded) {
        const { downloadBa } = useBestandsaufnahmens();

        // @ts-ignore
        await downloadBa(undefined, newBa.id, isNaN(newBa.immobilie) ? newBa.immobilie?.id : newBa.immobilie)
      }

      return state.currentBa;
    },

    async saveHzbaLocally({ state, dispatch }: { state: any; dispatch: any }, setToLocal = true) {
      try {
        if (!state.currentBa.isLocal) {
        const im = Immobilie.find(state.currentBa.immobilie);
          if (im) {
            im!.isDownloaded = true;
            await Immobilie.dispatch('$updateLocally', { data: im });
          }
        }

        state.currentBa.isLocal = setToLocal;
        state.currentBa.malus = state.currentBa.maengelReport()?.getCalculatedMalus()?.toString()
        state.currentBa.malusColor = state.currentBa.maengelReport()?.getColorByMaengel();
        state.currentBa.isDownloaded = true;

        console.log("SAVE HZBA LOCALLY")
        // logger.info(`{"message": "SAVE BA LOCALLY"}`);


        const ba = state.currentBa;
        await saveHzbaPhotosLocal(state.currentBa);

        await BestandsaufnahmeModel.update({
          where: ba.id,
          data: await ba.toClassJson(),
        });
        const uHzba = await BestandsaufnahmeModel.find(ba.id);

        await BestandsaufnahmeModel.dispatch('$updateLocally', { data: uHzba });
      } catch (err) {
        console.error('Error trying to save hzba locally: ', err);
        logger.error(`Error trying to save hzba locally: ${err}`);
      }
    },

    async confirmSuccessfullUpload({state, dispatch,}: { state: any; dispatch: any; }) {
      state.photoUploads = undefined;
    },

  },
  getters: {
    /**
     * Evaluates if user is allowed to edit hzbas
     * Roles allowed to edit hzbas are projektverwalter (1) and gutachter (3).
     * gutachter (3) are only allowed to edit assigned hzbas but because they cannot fetch not-assigned hzbas, we don't need to evaluate this here
     * Immobilienverwalter (2) are never allowed to edit hzbas, even if assigned to hzba
     *
     * @returns if current user is allowed to edit hzbas
     */
    hasHzbaEditPermission() {
      const { user } = useUser();

      // roles allowed to edit hzbas are projektverwalter (1) and gutachter (2).
      return [1, 3].includes(user.value?.role.id ?? -1);
    },

    isBaReadonly(state: any, getters: any, rootState: any, rootGetters: any) {
      const { user } = useUser();

      if (!state.currentBa?.bearbeitenderNutzer) {
        return true;
      }

      if (state.currentBa?.bearbeitenderNutzer && state.currentBa.bearbeitenderNutzer?.id !== user.value?.id) {
        return true;
      }

      if (state.currentBa?.bearbeitendesGeraetId && state.currentBa.bearbeitendesGeraetId !== rootGetters["app/deviceId"]) {
        return true;
      }

      return state.currentBa?.status === "FREIGEGEBEN" ||
        state.currentBa?.status === "ABGESCHLOSSEN" ||
        !getters.hasHzbaEditPermission
    },

    getHzbaFromMode(state: any) {
      return (mode: number) => {
        if (mode === undefined || mode === -1) {
          return state.currentBa;
        }
        return state.modalBas[mode];
      };
    },
  },
};
